// The rules the Users screen applies to its rows: the toolbar's three filters, and the
// shape of the CSV the export dialog writes. Kept out of the grid component so they can be
// exercised without mounting a DataGrid. usersListGrid.jsx is the only consumer.
//
// Named for the screen rather than for the export because most of what is here is display
// filtering, and because "export status" in this file means the DEALER's ServiceTitan invoice
// export state -- nothing to do with the CSV. A file called userExport.js whose dominant concept
// is EXPORT_STATUS_VALUES = ["Completed", "Exported", "Posted"] reads as though those were
// states of the download.

export const STATUS_ALL = "All";
export const STATUS_ACTIVE = "Active";
export const STATUS_INACTIVE = "InActive";

// Export status is a DEALER-level fact, not a user one: invoiceStatus as vwOnboardedDealers
// reports it, which the Dealer Lookup admin tool edits as "ST Data Pull" and which the user-list
// handler carries onto every user under that dealer (jbw-sam/src/jbwdealerregistrationlist/index.js).
// Every user under one dealer therefore reads the same value, and filtering by it selects whole
// dealers. Both screens read that one view, so neither can report a dealer the other contradicts.
export const EXPORT_STATUS_ALL = "All";
// A dealer that vwOnboardedDealers -- the view Dealer Lookup reads, and the one the handler now
// reads too -- carries no ACTIVE row for has no export status at all: a dealer that was never
// onboarded, one whose tenant has no tblTransmissionDetails row (its first two branches INNER JOIN
// on that), and the admin rows, which belong to no dealer. That is a state an admin needs to be
// able to ask for, so it is an option rather than a gap only reachable through All.
//
// An ONBOARDED Workiz or vendor dealer is deliberately NOT in this bucket. Those two branches of
// the view emit the literal 'Completed', and this screen shows what Dealer Lookup shows rather than
// second-guessing it -- see the handler for why that parity is by construction.
export const EXPORT_STATUS_NONE = "None";
// The values tblTenant.invoiceStatus takes; DealerAdminTool's ST Data Pull dropdown writes them --
// for ONE tenant at a time, while this column aggregates every active tenant of the dealer. A
// dealer with two active tenants therefore still reads "Exported, Posted" here after an admin has
// edited one of them on Dealer Lookup. Both screens are right; neither says so.
export const EXPORT_STATUS_VALUES = ["Completed", "Exported", "Posted"];

// A dealer can hold several active tenants, and they need not agree -- the handler reports every
// distinct value it found ("Exported, Posted") rather than picking one, so read it back as a list.
export const exportStatusesOf = (user) =>
  String(user.exportStatus ?? "")
    .split(",")
    .map((status) => status.trim())
    .filter(Boolean);

// Membership, not equality: a dealer sitting at both Exported and Posted is genuinely in both, so
// asking for either must return it. Equality here would make a mixed dealer unreachable from every
// option except All, which is the one case an admin chasing a half-migrated dealer is looking for.
export const matchesExportStatus = (user, exportStatusFilter) => {
  if (exportStatusFilter === EXPORT_STATUS_ALL) return true;
  const statuses = exportStatusesOf(user);
  if (exportStatusFilter === EXPORT_STATUS_NONE) return statuses.length === 0;
  return statuses.includes(exportStatusFilter);
};

// Narrows the user list by the three filters the toolbar offers. They AND together, an empty
// dealer ID means "every dealer", and All means "every export status".
export const filterUsers = (
  users,
  statusFilter,
  dealerIdFilter,
  exportStatusFilter = EXPORT_STATUS_ALL
) => {
  const dealerId = dealerIdFilter.trim();
  return users.filter((user) => {
    // isActive is a nullable tinyint with no default (jbwst-schema.sql:347), so
    // NULL is reachable. The Status chip already renders anything that is not 0
    // as Active, and that is the contract the admin can see -- match it here and
    // in the export, or a NULL row shows as Active while both filters hide it.
    if (statusFilter === STATUS_ACTIVE && user.isActive === 0) return false;
    if (statusFilter === STATUS_INACTIVE && user.isActive !== 0) return false;
    // Exact, so an admin can isolate one dealer: 131 returns neither 13101 nor
    // 9131. Admins carry dealerId "" (userscard.jsx maps dealerId 0 to "") and
    // so drop out once a dealer is named.
    if (dealerId && String(user.dealerId ?? "") !== dealerId) return false;
    if (!matchesExportStatus(user, exportStatusFilter)) return false;
    return true;
  });
};

// Excel and LibreOffice evaluate a cell starting with = + - @ or tab as a
// formula, and enclosing quotes do not stop it -- the sheet strips them first.
// registeredName and dealerName are chosen by whoever signs up (/signup sits
// outside RouteGuard, and jbwusersignup writes fullname straight through), and
// an admin is the one who opens the file, so neutralise them on the way out.
//
// Line breaks are flattened BEFORE the leading-character test, and that order is
// the whole defence. export-from-json only encloses a field matching
// /,|"|\n/ (converters.ts, `encloser`), so a lone CR is not a quoting trigger:
// "Gordon\r=cmd|'/C calc'!A0" starts with `G`, passes an anchored test
// untouched, and is written unquoted. Excel, Calc and Sheets all end a record on
// a bare CR, so the payload lands as the first cell of a row this code never
// inspected. Flattening first means there is only ever one cell to neutralise.
const csvSafe = (value) => {
  const flat = String(value).replace(/[\r\n]+/g, " ");
  return /^[=+\-@\t]/.test(flat) ? `'${flat}` : flat;
};

// The one reason a matching row is held back from the export. Named and exported
// because the dialog tells the admin WHY rows were dropped ("... are not opted in
// to email notifications"), and deriving that count arithmetically from the two
// list lengths would keep the sentence true only for as long as this stays the
// sole exclusion. Count with this predicate, and adding a second exclusion breaks
// a test rather than quietly making the dialog lie.
//
// emailOptIn is a nullable tinyint, so a null must not be read as consent.
// This matches ONE conjunct of the production emailer's predicate
// (db/stored-procedures/jbw_DealerEmailData.sql): it selects on
// isActive = 1 AND dealerId <> 0 AND emailOptIn = 1 AND a frequency window.
// The divergences are deliberate -- inactive users are exported by design
// (status filters the view, it does not decide mailability) and the
// frequency window is meaningless for a one-off announcement. Admin rows
// (dealerId 0) are kept, because the story asks for all users; the emailer
// would skip them, so an admin pasting this list mails more people than a
// scheduled send would.
export const isMailable = (row) => row.emailOptIn === 1;

// The columns an admin needs to paste into a mailing tool, and nothing else --
// naming the keys explicitly is also what keeps dealerAvatar and the actions
// column out of the file. Status is spelled as a word rather than exported as
// the underlying 1/0.
//
// includeNotOptedIn is the export dialog's opt-in checkbox. It defaults to FALSE, so every caller
// that does not ask for the wider list gets the opted-in-only file this has always written: the
// admin has to make the choice deliberately, in the dialog, per export.
export const buildExportRows = (rows, { includeNotOptedIn = false } = {}) =>
  rows
    .filter((row) => includeNotOptedIn || isMailable(row))
    .map((row) => ({
      Name: csvSafe(row.registeredName || ""),
      Email: csvSafe(row.registeredEmail || ""),
      "Dealer Name": csvSafe(row.dealerName || ""),
      // Derived from a numeric id and a fixed enum, so neither can carry a
      // formula and neither needs neutralising.
      "Dealer ID": row.dealerId === "" ? "" : String(row.dealerId ?? ""),
      Status: row.isActive === 0 ? STATUS_INACTIVE : STATUS_ACTIVE,
      // Only on the WIDENED export, and only there. emailOptIn = 0 is an affirmative decline, not
      // absent consent -- the production emailer honours it -- so a file carrying decliners has to
      // say per row which ones they are. The filename token is the only other signal and it does
      // not survive a rename, a paste of the rows into a mailing tool, or a copy of the Email
      // column; there is no server-side record either, since the widened export is entirely
      // client-side. Spread rather than a null column so the DEFAULT file keeps its exact five
      // columns and every existing consumer of it is untouched.
      ...(includeNotOptedIn ? { "Email Opt In": isMailable(row) ? "Yes" : "No" } : {}),
    }));
